import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { withPromiseTimeout } from "@/lib/promise-timeout";

export const MEMBER_MFA_FACTORS_TIMEOUT_MS = 15_000;

export interface MfaFactor {
  id: string;
  factor_type: "totp" | "phone" | "webauthn";
  friendly_name: string | null;
  status: "verified" | "unverified";
  created_at: string;
}

export function useMemberMfaFactors() {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ["member-mfa-factors", user?.id],
    queryFn: async () => {
      const { data, error } = await withPromiseTimeout(
        supabase.auth.mfa.listFactors(),
        MEMBER_MFA_FACTORS_TIMEOUT_MS,
        "Timed out while loading MFA factors",
      );
      if (error) {
        throw error;
      }

      return (data?.all ?? []) as unknown as MfaFactor[];
    },
    staleTime: 0,
    refetchOnMount: "always",
    enabled: !!user,
  });

  return {
    ...query,
    // Only report loading on the initial fetch (no cached data yet).
    // Background refetches (isFetching=true with cached data) should NOT
    // block callers like useMfaRequirement / ProtectedRoute — the stale
    // cached result is perfectly fine while the refetch is in-flight.
    isLoading: query.isLoading,
    isFetching: query.isFetching,
  };
}
